import {
  Box,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from "@mui/material"
import { groupBy, type DataSourceType } from "@oboku/shared"
import { Fragment, memo, useMemo } from "react"
import { Logger } from "../debug/logger.shared"
import { TopBarNavigation } from "../navigation/TopBarNavigation"
import { BuildRounded, DeleteForeverRounded } from "@mui/icons-material"
import { useFixCollections } from "./useFixCollections"
import { Alert } from "@mui/material"
import { useObserve } from "reactjrx"
import { latestDatabase$ } from "../rxdb/RxDbProvider"
import { switchMap } from "rxjs"
import { getCollectionComputedMetadata } from "../collections/getCollectionComputedMetadata"
import { useFixableCollections } from "./useFixableCollections"
import { useRepair } from "./useRepair"
import { CollectionDanglingBooks } from "./CollectionDanglingBooks"
import { useFixableBooks } from "./useFixableBooks"
import { BookDanglingCollections } from "./BookDanglingCollections"
import { BookDanglingLinks } from "./BookDanglingLinks"
import { useFixableLinks } from "./useFixableLinks"
import { PageContainer } from "../common/PageContainer"
import { PageSection } from "../common/PageSection"

const COLLECTION_IDENTITY_FIELDS: Record<DataSourceType, readonly string[]> = {
  DRIVE: ["fileId"],
  dropbox: ["fileId"],
  "one-drive": ["driveId", "fileId"],
  webdav: ["connectorId", "filePath"],
  "synology-drive": ["connectorId", "fileId"],
  server: ["connectorId", "filePath"],
  URI: ["url"],
  file: ["filename"],
}

export const ProblemsScreen = memo(function ProblemsScreen() {
  const fixCollections = useFixCollections()
  const { danglingLinks } = useFixableLinks()
  const { collectionsWithDanglingBooks } = useFixableCollections()
  const { booksWithDanglingCollections, booksWithDanglingLinks } =
    useFixableBooks()
  const { mutate: repair } = useRepair()
  const { data: collections = [] } = useObserve(
    () => latestDatabase$.pipe(switchMap((db) => db.obokucollection.find().$)),
    [],
  )

  const duplicatedCollections = useMemo(() => {
    const collectionKey = (c: (typeof collections)[0]) => {
      const identityFields = c.linkType
        ? COLLECTION_IDENTITY_FIELDS[c.linkType]
        : undefined

      const identityEntries = c.linkData
        ? Object.entries(c.linkData)
            .filter(([key]) => !identityFields || identityFields.includes(key))
            .sort(([a], [b]) => a.localeCompare(b))
        : []

      return JSON.stringify({
        linkType: c.linkType,
        linkData: Object.fromEntries(identityEntries),
      })
    }
    const collectionsByKey = groupBy(collections, collectionKey)
    const keys = Object.keys(collectionsByKey)
    const duplicatedCollections = keys
      .filter((key) => (collectionsByKey[key]?.length ?? 0) > 1)
      .map((key) => {
        const _collections = collectionsByKey[key] ?? []
        const collection = _collections[0]

        return [
          key,
          {
            name: getCollectionComputedMetadata(collection?.toJSON())?.title,
            number: collectionsByKey[key]?.length,
          },
        ]
      })

    Logger.log(
      `Found ${duplicatedCollections.length} duplicated resource id`,
      duplicatedCollections,
    )

    return duplicatedCollections as [string, { name: string; number: number }][]
  }, [collections])

  return (
    <>
      <TopBarNavigation title={"Problems finder"} showBack={true} />
      <Box
        sx={{
          overflow: "auto",
          flex: 1,
        }}
      >
        <PageContainer>
          <Alert severity="warning" sx={{ mt: 2 }}>
            PLEASE READ THIS:
            <p>
              This is an <b>EXPERIMENTAL</b> section which can help you find
              anomaly or integrity issues. However not all anomalies are due to
              system errors. For example if we detect duplicated book with same
              title, it does not necessarily means we created a duplicate by
              error. You could have created two different book with the same
              title. In this situation, if you try to resolve the problem
              automatically we might end up merging two different book whereas
              you needed to update the title of one of them.
            </p>
            <p>
              Most repairs are non destructive and will try to use or merge data
              with their latest state. For example if we fix duplicate books, we
              will make sure to prioritize the book with latest reading state.
              That being said, nothing is perfect so try to fix things by hand
              when necessary.
            </p>
            <p>
              <b>
                Before doing anything here make sure to have been connected to
                the internet and synced. In addition, make sure to to have sync
                your data source recently. Make sure to have sync all your
                devices if needed. THIS IS IMPORTANT.
              </b>
            </p>
          </Alert>
        </PageContainer>
        <PageContainer disableGutters>
          <PageSection>
            {duplicatedCollections.length > 0 && (
              <ListItem alignItems="flex-start">
                <ListItemText
                  primary="Duplicated collection names"
                  secondary={duplicatedCollections.map(
                    ([id, { name, number }]) => (
                      <Fragment key={id}>
                        (x{number}) {name} <br />
                      </Fragment>
                    ),
                  )}
                />
              </ListItem>
            )}
            {duplicatedCollections.length > 0 && (
              <ListItemButton
                alignItems="flex-start"
                onClick={() => fixCollections(duplicatedCollections)}
              >
                <ListItemIcon>
                  <BuildRounded />
                </ListItemIcon>
                <ListItemText
                  primary="Duplicated collections from same resources"
                  secondary={`
              We found ${duplicatedCollections.length} same resources that are used by more than one collection. 
              This means that some of your collections should probably be merged together since they use the same origin.
              It is not recommended to keep duplicates since it could lead to unpredictable sync.
              `}
                />
              </ListItemButton>
            )}
            {danglingLinks.length > 0 && (
              <ListItemButton
                alignItems="flex-start"
                onClick={() =>
                  repair({
                    type: "danglingLinks",
                    items: danglingLinks,
                  })
                }
              >
                <ListItemIcon>
                  <DeleteForeverRounded />
                </ListItemIcon>
                <ListItemText
                  primary="Dangling links"
                  secondary={`
            We found ${danglingLinks.length} links not used by any books. They just take space and will not be used anymore. Safe to remove.
            `}
                />
              </ListItemButton>
            )}
            {booksWithDanglingCollections?.map(({ danglingItems, doc }) => (
              <BookDanglingCollections
                key={doc._id}
                danglingBooks={danglingItems}
                doc={doc}
                onClick={() =>
                  repair({
                    danglingItems,
                    doc,
                    type: "bookDanglingCollections",
                  })
                }
              />
            ))}
            {collectionsWithDanglingBooks?.map(({ doc, danglingItems }) => (
              <CollectionDanglingBooks
                key={doc._id}
                danglingBooks={danglingItems}
                doc={doc}
                onClick={() =>
                  repair({
                    danglingItems,
                    doc,
                    type: "collectionDanglingBooks",
                  })
                }
              />
            ))}
            {booksWithDanglingLinks?.map(({ doc, danglingItems }) => (
              <BookDanglingLinks
                key={doc._id}
                danglingBooks={danglingItems}
                doc={doc}
                onClick={() =>
                  repair({
                    danglingItems,
                    doc,
                    type: "bookDanglingLinks",
                  })
                }
              />
            ))}
          </PageSection>
        </PageContainer>
      </Box>
    </>
  )
})
